import React, { useRef, useState } from 'react';
import FamilyDialog from '../family/FamilyDialog';
import { callDecideFamilyChangeRequest } from './decideFamilyChangeRequestClient';

const dollars = (cents: number) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

interface RejectFamilyRequestDialogProps {
  requestId: string;
  /** Whether this request has already been paid (amountDueCents > 0 and paymentReceived). */
  isPaid: boolean;
  amountDueCents: number;
  onClose: () => void;
  onRejected: (message: string) => void;
}

/**
 * Reject confirmation for a FamilyChangeRequest (#127 slice 5). Requires a
 * reason. When the request was already paid, shows "Refund $<amount>
 * separately in Square." both before and after confirming, since Square
 * refunds aren't automated here.
 */
const RejectFamilyRequestDialog: React.FC<RejectFamilyRequestDialogProps> = ({
  requestId,
  isPaid,
  amountDueCents,
  onClose,
  onRejected,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [rejected, setRejected] = useState(false);

  const refundText = `Refund ${dollars(amountDueCents)} separately in Square.`;

  const handleReject = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const result = await callDecideFamilyChangeRequest({
        requestId,
        decision: 'REJECT',
        reason: reason.trim(),
      });
      if (result.success) {
        setRejected(true);
        onRejected(result.message || 'Request rejected.');
        return;
      }
      setServerError(result.message || 'Could not reject this request. Please try again.');
    } catch (err: any) {
      setServerError(err?.message || 'Could not reject this request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FamilyDialog
      role="alertdialog"
      titleId="reject-family-request-title"
      title="Reject this request?"
      descriptionId="reject-family-request-desc"
      busy={submitting}
      onClose={onClose}
    >
      <div id="reject-family-request-desc" className="mt-2 space-y-2 text-sm text-gray-700">
        <p>The member will be told why their request was not approved.</p>
        {isPaid && (
          <p
            role="alert"
            className="rounded-md border border-amber-300 bg-amber-50 p-2 font-medium text-amber-900"
          >
            {refundText}
          </p>
        )}
      </div>

      <div className="mt-3">
        <label htmlFor="reject-reason" className="block text-sm font-medium text-gray-700 mb-1">
          Reason *
        </label>
        <input
          id="reject-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={rejected}
          className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
        />
      </div>

      {serverError && (
        <p role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm font-medium text-red-700">
          {serverError}
        </p>
      )}

      {rejected && isPaid && (
        <p role="alert" className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm font-medium text-amber-900">
          {refundText}
        </p>
      )}

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          ref={cancelRef}
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
        >
          {rejected ? 'Close' : 'Cancel'}
        </button>
        {!rejected && (
          <button
            type="button"
            onClick={handleReject}
            disabled={submitting || !reason.trim()}
            className="min-h-[44px] rounded-lg bg-red-700 px-4 py-2 font-semibold text-white hover:bg-red-800 disabled:opacity-50"
          >
            {submitting ? 'Rejecting…' : 'Reject request'}
          </button>
        )}
      </div>
    </FamilyDialog>
  );
};

export default RejectFamilyRequestDialog;
